import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Chip } from "@mui/material";
import { OverflowTooltip, Table } from "@4uhub/lib4uhub";
import MainApi from "../../../../../../services/mainApi.service";
import { useCallback, useMemo, useState } from "react";
import { ITicketAutomationReceivedSlot } from "../../../../../../models/ticket-automation-received-slot";
import AutomationReceivedSlot from "./AutomationReceivedSlotForm/AutomationReceivedSlot";

const TICKET_AUTOMATION_RECEIVED_SLOT_ROUTE =
  process.env.REACT_APP_AUTOMATIONS + "/api/v1/TicketAutomationReceivedSlot/";
const ticketAutomationReceivedSlotService =
  new MainApi<ITicketAutomationReceivedSlot>(
    TICKET_AUTOMATION_RECEIVED_SLOT_ROUTE
  );

const translationPath =
  "chatBot.page.automations.automationReceivedRules.automationReceivedSlot.";

function joinExternalNames(
  items?: Array<{ calendarExternalName?: string | null }> | null
): string {
  if (!items?.length) return "-";
  const seen = new Set<string>();
  const out: string[] = [];
  for (const it of items) {
    const name = (it?.calendarExternalName ?? "").trim();
    if (!name) continue;
    const key = name.toLocaleLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out.length ? out.join("; ") : "-";
}

const AutomationReceivedSlotPage = ({
  automationCode,
}: {
  automationCode?: string;
}) => {
  const { t } = useTranslation();

  const { id: ticketAutomationReceivedRuleId } = useParams();

  const [timeRuleForm, setTimeRuleForm] = useState(false);

  const [selectedId, setSelectedId] = useState<string>();

  const columns: GridColDef<ITicketAutomationReceivedSlot>[] = [
    {
      field: "calendarSlotSortType",
      headerName:
        t(translationPath + "calendar_slot_sort_type") ||
        "Calendar Slot Sort Type",
      renderCell: (params) =>
        params.row.calendarSlotSortType
          ? params.row.calendarSlotSortType.name
          : "-",
    },
    {
      field: "ticketAutomationReceivedSlotCalendars",
      headerName:
        t(translationPath + "ticketAutomationReceivedSlotCalendars") ||
        "Calendars",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const text = joinExternalNames(params.value);
        return <OverflowTooltip text={text} tooltip={text} />;
      },
    },
    {
      field: "slotsAvailable",
      headerName: t(translationPath + "slots_available") || "Slots Available",
    },
    {
      field: "priority",
      headerName: t(translationPath + "priority") || "Priority",
    },
    {
      field: "enable",
      headerName: t(translationPath + "status") || "Status",
      maxWidth: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          label={
            params.value
              ? t(translationPath + "active")
              : t(translationPath + "inactive")
          }
          color={params.value ? "success" : "error"}
        />
      ),
    },
  ];

  const handleAdd = () => {
    setTimeRuleForm(true);
  };

  const handleRowClick = (
    params: GridRowParams<ITicketAutomationReceivedSlot>
  ) => {
    if (!params.row) return;
    setTimeRuleForm(true);
    setSelectedId(params.row.id);
  };

  const handleBack = useCallback(() => {
    setSelectedId(undefined);
    setTimeRuleForm(false);
  }, []);
  const props = useMemo(() => {
    return {
      TicketAutomationReceivedRuleId: ticketAutomationReceivedRuleId,
    };
  }, []);

  return (
    <Box sx={{ height: 600, width: "100%" }}>
      {timeRuleForm && (
        <Box
          sx={(t) => ({
            p: 1,
            px: 10,
            width: "100%",
            height: "100%",
            overflow: "auto",
            backgroundColor: t.palette.background.default,
            borderRadius: 1,
          })}
        >
          <AutomationReceivedSlot
            id={selectedId}
            automationCode={automationCode}
            ticketAutomationReceivedRuleId={ticketAutomationReceivedRuleId}
            onBackButton={handleBack}
          />
        </Box>
      )}
      {!timeRuleForm && (
        <Box height={600}>
          <Table<ITicketAutomationReceivedSlot>
            showDefaultMacroFilters={false}
            service={ticketAutomationReceivedSlotService}
            columns={columns}
            title={""}
            searchInput
            onAdd={handleAdd}
            onRowClick={handleRowClick}
            searchInputAutoFocus={false}
            defaultPageSize={20}
            serviceProps={props}
            pageSizeOptions={[5, 10, 20]}
            searchInputPlaceHolder={""}
            addButtonLabel={
              t(translationPath + "add_slot_schedule") || "Add Slot Schedule"
            }
            confirmOptions={{
              title: t(translationPath + "confirm.title"),
              description: t(translationPath + "confirm.description"),
              confirmationButtonProps: {
                variant: "contained",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default AutomationReceivedSlotPage;
